import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setToken } from './api';

export interface Persona {
  id: string;
  name: string;
  title: string;
  org: string;
  /**
   * Module-scoped role codes: COURT_CLERK, REGISTRAR_COURT, JUDGE,
   * TRIBUNAL_MEMBER (M37 Judiciary); LAW_REVISION_COMMISSIONER,
   * GAZETTE_EDITOR, LEGISLATIVE_DRAFTER (M38 Attorney General & Legislation).
   */
  roles: string[];
  trustTier?: string;
}

export interface AuthConfig {
  oneCitizen: boolean;
  devLogin: boolean;
}

interface AuthState {
  persona: Persona | null;
  personas: Persona[];
  config: AuthConfig;
  loading: boolean;
  login: (personaId: string) => Promise<void>;
  /** Store a session issued by the gateway (One Citizen sign-in). */
  completeLogin: (token: string, persona: Persona) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [persona, setPersona] = useState<Persona | null>(() => {
    const raw = localStorage.getItem('justice.persona');
    return raw ? (JSON.parse(raw) as Persona) : null;
  });
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [config, setConfig] = useState<AuthConfig>({ oneCitizen: false, devLogin: true });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<{ items: Persona[] }>('/auth/personas').catch(() => ({ items: [] as Persona[] })),
      api.get<AuthConfig>('/auth/config').catch(() => ({ oneCitizen: false, devLogin: true })),
    ])
      .then(([p, c]) => {
        setPersonas(p.items);
        setConfig(c);
      })
      .finally(() => setLoading(false));
  }, []);

  const completeLogin = useCallback((token: string, p: Persona) => {
    setToken(token);
    localStorage.setItem('justice.persona', JSON.stringify(p));
    setPersona(p);
  }, []);

  const login = useCallback(
    async (personaId: string) => {
      const r = await api.post<{ token: string; persona: Persona }>('/auth/dev-login', { personaId });
      completeLogin(r.token, r.persona);
    },
    [completeLogin],
  );

  const logout = useCallback(() => {
    setToken(null);
    localStorage.removeItem('justice.persona');
    setPersona(null);
  }, []);

  const value = useMemo(
    () => ({ persona, personas, config, loading, login, completeLogin, logout }),
    [persona, personas, config, loading, login, completeLogin, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
